import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';

const ITEMS_PER_PAGE = 20;

const { debug } = useDebug(DEBUG_NAMESPACE.STORE);

export interface CollectionItemInterface {
  id: string;
  name: string;
  hasSecurity: boolean;
  security?: {
    sharing: string;
    password: string;
    passwordProtected: boolean;
    expires: number;
    expiresAfterViews: number;
    expiresAt: string;
    expired: boolean;
    specificUsers: string[];
  };
  onboarding: boolean;
  author: string;
  shareUrl: string;
  //itemsCount: number; use items.length instead
  viewCount: number;
  createdAt: Date;
  updatedAt: Date;
  items: string[];
}

export interface CollectionState {
  currentPage: number;
  totalCount: number;
  isLoading: boolean;
  isSaving: boolean;
  toggleSaveState: string[]; // store currently saving state for each item in collection
  list: CollectionItemInterface[];
}

export const useCollectionsStore = defineStore('collectionsStore', {
  state: (): CollectionState => ({
    isLoading: false,
    isSaving: false,
    currentPage: 1,
    totalCount: 0,
    toggleSaveState: [],
    list: [],
  }),
  actions: {
    parseList(data: any) {
      return data.map((el: any) => {
        return {
          id: el.id,
          ...el.attributes,
          createdAt: el.attributes.createdAt ? new Date(el.attributes.createdAt) : undefined,
          updatedAt: el.attributes.updatedAt ? new Date(el.attributes.updatedAt) : undefined,
          items: el.relationships.items.data.map((item: any) => item.id),
          author: el.relationships.user.meta.email,
        };
      });
    },
    async load() {
      try {
        this.isLoading = true;
        this.$patch({ currentPage: 1, list: [], totalCount: 0 });

        return Api.collections.list(this.currentPage, ITEMS_PER_PAGE).then((res: any) => {
          if (!res.data.collections || res.data.collections.length === 0) {
            return Promise.resolve();
          }

          this.$patch({
            list: this.parseList(res.data.collections),
            totalCount: res.data.totalCount,
          });

          this.isLoading = false;
        });
      } catch (e) {
        console.error('[useCollectionsStore] Cannot load feature list');
        debug(e);

        return Promise.reject('Cannot load collections list');
      }
    },
    async loadMore() {
      if (this.isLoadedAll || this.isLoading) {
        return Promise.resolve();
      }

      this.$patch({ currentPage: this.currentPage + 1 });

      this.isLoading = true;

      return Api.collections.list(this.currentPage, ITEMS_PER_PAGE).then((res: any) => {
        this.$patch({
          list: [...this.list, ...this.parseList(res.data.collections)],
          totalCount: res.data.totalCount,
        });

        this.isLoading = false;
      });
    },
    async create(name: string, itemId: string) {
      this.isSaving = true;

      return Api.collections.create(name).then(res => {
        this.isSaving = false;

        if (!res.data.collection) {
          return Promise.resolve();
        }

        this.$patch({
          list: [...this.parseList([res.data.collection]), ...this.list],
          totalCount: this.totalCount + 1,
        });
        return this.addItem(itemId, res.data.collection.id);
      });
    },
    async toggleItem(itemId: string, collectionId: string) {
      if (this.toggleSaveState.includes(collectionId)) {
        return Promise.resolve();
      }

      const collection = this.list.find(el => el.id === collectionId);

      if (!collection) {
        return Promise.reject('Collection not found');
      }

      if (collection.items.includes(itemId)) {
        return this.removeItem(itemId, collectionId);
      }

      return this.addItem(itemId, collectionId);
    },
    async addItem(itemId: string, collectionId: string) {
      this.toggleSaveState.push(collectionId);

      return Api.collections.addItem(itemId, collectionId).then(() => {
        this.toggleSaveState = this.toggleSaveState.filter(el => el !== collectionId);

        return this.$patch({
          list: this.list.map(el => {
            if (el.id === collectionId) {
              return {
                ...el,
                items: [...el.items, itemId],
              };
            }

            return el;
          }),
        });
      });
    },
    async removeItem(itemId: string, collectionId: string) {
      this.toggleSaveState.push(collectionId);

      return Api.collections.removeItem(itemId, collectionId).then(() => {
        this.toggleSaveState = this.toggleSaveState.filter(el => el !== collectionId);

        return this.$patch({
          list: this.list.map(el => {
            if (el.id === collectionId) {
              return {
                ...el,
                items: el.items.filter(el => el !== itemId),
              };
            }

            return el;
          }),
        });
      });
    },
    reset() {},
  },
  getters: {
    isLoadedAll: (state: CollectionState) => state.currentPage * ITEMS_PER_PAGE >= state.totalCount,
    filteredList: (state: CollectionState) =>
      state.list.filter((col: CollectionItemInterface) => {
        if (col.onboarding) {
          return false;
        }

        // only if organization can edit collection show it
        if (col.hasSecurity && col.security && !['oe', 'un'].includes(col.security.sharing)) {
          return false;
        }

        return true;
      }),
  },
});
